import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldX, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

export default function VerificationPage() {
  const [reportId, setReportId] = useState("");
  const [sha256, setSha256] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const verify = async (e) => {
    e.preventDefault();
    if (!reportId || !sha256) return toast.error("Both Report reference and SHA-256 hash are required");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/verifications/report", { report_id: reportId.trim(), sha256: sha256.trim() });
      setResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="verification-page">
      <div>
        <div className="tiny-label">Compliance · audit verification</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Report verification</h1>
        <p className="text-muted-foreground mt-1">
          Paste a Report reference and SHA-256 from a signed PDF to confirm it matches the audit log.
        </p>
      </div>

      <Alert className="border-blue-200 bg-blue-50 text-blue-800">
        <FileCheck2 className="w-4 h-4" />
        <AlertTitle>What this verifies</AlertTitle>
        <AlertDescription>
          This confirms the integrity stamp on a signed PDF was created by HR Track and has not been altered.
          It is an internal audit-tracking mechanism — not a certified or legally binding digital signature.
        </AlertDescription>
      </Alert>

      <Card className="border-border shadow-none">
        <CardContent className="p-6">
          <form onSubmit={verify} className="grid sm:grid-cols-12 gap-4 items-end">
            <div className="sm:col-span-3">
              <Label>Report reference</Label>
              <Input
                value={reportId}
                onChange={(e) => setReportId(e.target.value.toUpperCase())}
                placeholder="RPT-XXXXXX-YYYY"
                className="font-mono"
                data-testid="verify-report-id"
              />
            </div>
            <div className="sm:col-span-7">
              <Label>SHA-256 hash</Label>
              <Input
                value={sha256}
                onChange={(e) => setSha256(e.target.value.toLowerCase())}
                placeholder="64-character hex string"
                className="font-mono text-xs"
                data-testid="verify-sha256"
              />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy} className="w-full h-10" data-testid="verify-submit">
                {busy ? "Verifying…" : "Verify"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {result && result.verified && (
        <Card className="border-emerald-200 bg-emerald-50/40 shadow-none" data-testid="verify-success">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-emerald-700" />
              <h3 className="font-display font-semibold text-lg text-emerald-800">Report verified</h3>
              <Badge className="ml-auto bg-emerald-600 text-white border-0">Authentic</Badge>
            </div>
            <dl className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <div><dt className="tiny-label mb-0.5">Generated by</dt><dd>{result.generated_by_email}</dd></div>
              <div><dt className="tiny-label mb-0.5">Generated at</dt><dd className="font-mono text-xs">{new Date(result.generated_at).toLocaleString()}</dd></div>
              <div><dt className="tiny-label mb-0.5">Report type</dt><dd className="capitalize">{result.scope}</dd></div>
              <div><dt className="tiny-label mb-0.5">Date range</dt><dd className="font-mono">{result.start} → {result.end}</dd></div>
              <div className="sm:col-span-2"><dt className="tiny-label mb-0.5">Filters used</dt><dd className="font-mono text-xs">{Object.keys(result.filters || {}).length ? JSON.stringify(result.filters) : "none"}</dd></div>
              <div className="sm:col-span-2"><dt className="tiny-label mb-0.5">Filename</dt><dd className="font-mono text-xs">{result.filename}</dd></div>
              <div className="sm:col-span-2"><dt className="tiny-label mb-0.5">Row count</dt><dd>{result.row_count}</dd></div>
            </dl>
          </CardContent>
        </Card>
      )}
      {result && !result.verified && (
        <Card className="border-red-200 bg-red-50/40 shadow-none" data-testid="verify-failure">
          <CardContent className="p-6 flex items-start gap-3">
            <ShieldX className="w-5 h-5 text-red-700 mt-0.5" />
            <div>
              <h3 className="font-display font-semibold text-lg text-red-800">Not verified</h3>
              <p className="text-sm text-red-800/80 mt-1">{result.reason || "The Report reference or hash did not match any audit record."}</p>
              <p className="text-xs text-muted-foreground mt-3">Check that you copied both fields exactly from the signed PDF. The hash must be a 64-character hex string.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
